import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Receipt, Loader2 } from 'lucide-react';
import { purchaseOrdersAPI } from '../../services/api';

const DELIVERY_TYPE_LABELS = {
  session: 'Private Delivery',
  partner_conference: 'Partner Conference',
  regional_in_person: 'Regional In-Person',
};

const formatCurrency = (v) => `$${(v || 0).toLocaleString()}`;

// In-app "Invoices Due Soon" reminder — completed deliveries with no invoiced amount yet
// (within 15 days of today), plus PO line items with a planned delivery date due within 15 days.
export const InvoicesDueSoonWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    purchaseOrdersAPI.getInvoicesDueSoon()
      .then(setData)
      .catch(() => setData({ completed_uninvoiced: [], line_items_due: [] }))
      .finally(() => setLoading(false));
  }, []);

  const completed = data?.completed_uninvoiced || [];
  const lineItems = data?.line_items_due || [];
  const totalCount = completed.length + lineItems.length;

  if (loading) {
    return (
      <Card data-testid="invoices-due-soon-widget">
        <CardContent className="pt-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />Checking invoices due soon...
        </CardContent>
      </Card>
    );
  }

  if (totalCount === 0) return null;

  return (
    <Card data-testid="invoices-due-soon-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Receipt className="w-4 h-4 text-lime-600" />
          Invoices Due Soon
          <Badge variant="outline" data-testid="invoices-due-soon-count">{totalCount}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {completed.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Recently completed, not yet invoiced</p>
            <div className="space-y-1.5">
              {completed.slice(0, 5).map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm border rounded-lg px-3 py-1.5" data-testid={`invoice-due-completed-row-${i}`}>
                  <span className="truncate">{c.title} <span className="text-muted-foreground">({DELIVERY_TYPE_LABELS[c.delivery_type] || c.delivery_type})</span></span>
                  <span className="text-muted-foreground flex-shrink-0 text-xs">{c.date}{c.po_number ? ` · ${c.po_number}` : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {lineItems.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">PO line items due within 15 days</p>
            <div className="space-y-1.5">
              {lineItems.slice(0, 5).map((li, i) => (
                <div key={i} className="flex items-center justify-between text-sm border rounded-lg px-3 py-1.5" data-testid={`invoice-due-line-item-row-${i}`}>
                  <span className="truncate">{li.description} <span className="text-muted-foreground">({li.po_number})</span></span>
                  <span className="text-muted-foreground flex-shrink-0 text-xs">{li.delivery_date} · {formatCurrency(li.remaining)} left</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InvoicesDueSoonWidget;
